import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, IndianRupee, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { apiMultipart } from '../utils/api';

const RaiseInvoiceModal = ({ payment, open, onClose, onSuccess }) => {
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!invoiceFile) {
      toast.error('Please upload an invoice file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('invoice', invoiceFile);
      formData.append('notes', notes);

      await apiMultipart.post(`/payments/${payment.payment_id}/raise-invoice`, formData);
      
      toast.success('Invoice raised successfully');
      setInvoiceFile(null);
      setNotes('');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setInvoiceFile(null);
    setNotes('');
    onClose();
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">Raise Invoice</DialogTitle>
          <DialogDescription className="text-stone-500">
            Upload invoice for milestone payment
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2" data-testid="raise-invoice-form">
          {/* Payment Summary */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">Milestone</span>
              <span className="font-medium text-stone-900">{payment.stage_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center">
                <IndianRupee className="h-3.5 w-3.5 mr-1" />
                Amount
              </span>
              <span className="text-xl font-bold text-stone-900">
                ₹{payment.amount.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                Due Date
              </span>
              <span className="font-medium text-stone-900">
                {new Date(payment.due_date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </span>
            </div>
          </div>

          {/* Invoice Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-stone-700">
              Invoice Document
            </Label>
            <div className="border-2 border-dashed border-stone-200 rounded-lg p-6 text-center hover:border-stone-300 transition-colors">
              <Input
                id="invoice"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setInvoiceFile(e.target.files[0])}
                className="hidden"
                data-testid="invoice-file-input"
              />
              <label htmlFor="invoice" className="cursor-pointer">
                {invoiceFile ? (
                  <div className="flex items-center justify-center text-stone-700">
                    <FileText className="h-8 w-8 mr-3 text-orange-600" />
                    <div className="text-left">
                      <p className="font-medium">{invoiceFile.name}</p>
                      <p className="text-sm text-stone-500">
                        {(invoiceFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-stone-400 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">Click to upload invoice</p>
                    <p className="text-sm text-stone-400 mt-1">PDF, JPG or PNG up to 10MB</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-stone-700">
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes for the customer..."
              rows={3}
              className="border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg resize-none"
              data-testid="invoice-notes-input"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
              disabled={uploading || !invoiceFile}
              data-testid="submit-invoice-button"
            >
              {uploading ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center">
                  <Upload className="mr-2 h-4 w-4" />
                  Send Invoice
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RaiseInvoiceModal;
